import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { FlexRow } from '@/atoms/FlexContainers'
import { BlockedIcon } from '@/atoms/Icons/BlockedIcon'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { CircleStarIcon } from '@/atoms/Icons/CircleStarIcon'
import { GuildSolidIcon } from '@/atoms/Icons/GuildSolidIcon'
import { Popover } from '@/atoms/Popover'
import { CaptionMD } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { DiscountPill } from '@/views/TicketCheckoutViews/ShowtimesView/components/DiscountPill'
import { PremierePartnerPill } from '@/views/TicketCheckoutViews/ShowtimesView/components/PremierePartnerPill'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { isPremierePartnerVenue } from '../utils/premierePartnerUtils'

interface Props {
  className?: string
  guildTicketsCount?: number
  isAngelPartnerVenue: boolean
  theaterSlug: string
  venue: ShowtimesVenue
  noOnlineTicketing?: boolean
}

const VenuePills: FC<Props> = ({
  className,
  guildTicketsCount,
  isAngelPartnerVenue,
  theaterSlug,
  venue,
  noOnlineTicketing = false,
}) => {
  const { t } = useTranslate('tickets')
  const { isFreeTicketPromo } = useTicketsContext()
  const { promo, experiments } = useTheatricalPromotionContext()
  const { promoType } = promo ?? {}

  const discount = useMemo(() => {
    return promo?.publicDiscount?.activeChildDiscount?.value ?? 0
  }, [promo?.publicDiscount])

  const isPremierePartner = isPremierePartnerVenue({
    vendorId: venue.vendorId,
    theaterSlug,
    experimentValue: experiments?.['premier-venue-pill-badge'],
  })
  if (noOnlineTicketing) {
    return (
      <FlexRow className={classNames('gap-2 flex-wrap experiment-hidden', className)}>
        <Popover
          className="!z-50"
          title={t('noOnlineTicketingUnavailable', 'Online Ticketing Unavailable')}
          description={t(
            'onlineTicketingIsNotAvailableForThisTheater',
            'Online ticketing is not available for this theater. Please visit the theater in person to purchase your tickets. Enjoy the show!',
          )}
          icon={<BlockedIcon size={20} color="warm-01" />}
          triggerComponent={
            <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
              <BlockedIcon size={14} color="core-gray-800" />
              <CaptionMD color="core-gray-800">
                {t('noOnlineTicketingUnavailable', 'Online Ticketing Unavailable')}
              </CaptionMD>
            </span>
          }
        />
      </FlexRow>
    )
  }

  if (!isAngelPartnerVenue && !isFreeTicketPromo) {
    return (
      <FlexRow className={classNames('gap-2 flex-wrap experiment-hidden', className)}>
        <Popover
          className="!z-50"
          title={t('discountsUnavailable', 'Discounts Unavailable')}
          description={t(
            'benefitsUnavailableDescriptionV2',
            'This theater is not an Angel Partner Theater. Discounts, coupon codes, Flexible Booking™, and Guild ticket redemptions are not available at this time through Angel.com.',
          )}
          icon={<BlockedIcon size={20} color="warm-01" />}
          triggerComponent={
            <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
              <BlockedIcon size={14} color="core-gray-800" />
              <CaptionMD color="core-gray-800">{t('discountsUnavailable', 'Discounts Unavailable')}</CaptionMD>
            </span>
          }
        />
      </FlexRow>
    )
  }

  return (
    <FlexRow className={classNames('gap-2 flex-wrap experiment-hidden', className)}>
      {isPremierePartner && <PremierePartnerPill />}
      {venue.isPremiumPartner ? (
        <Popover
          className="!z-30"
          title={t('premiumAngelPartner', 'Premium Angel Partner')}
          description={t(
            'premiumPartnerDescription',
            "This theater helps ignite Angel's mission to amplify light through remarkable cinematic experiences. Enjoy discounts, coupons, Guild ticket redemptions, and flexible booking.",
          )}
          icon={<CircleStarIcon color="warm-03" size={20} />}
          triggerComponent={
            <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
              <CircleStarIcon color="warm-03" size={14} />
              <CaptionMD color="core-gray-800">{t('premiumAngelPartner', 'Premium Angel Partner')}</CaptionMD>
            </span>
          }
        />
      ) : (
        <Popover
          className="!z-30"
          title={t('angelPartner', 'Angel Partner')}
          description={t(
            'angelPartnerDescriptionV3',
            'Angel Partner Theaters offer discounts, coupons, Flexible Booking, and Guild ticket redemptions. Only available at Angel.com.',
          )}
          icon={<CircleStarIcon color="white" size={20} />}
          triggerComponent={
            <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
              <CircleStarIcon color="core-gray-800" size={14} />
              <CaptionMD color="core-gray-800">{t('angelPartner', 'Angel Partner')}</CaptionMD>
            </span>
          }
        />
      )}
      <Popover
        className="!z-20"
        title={t('flexibleBooking', 'Flexible Booking™')}
        description={t(
          'flexibleBookingDescriptionPillv2',
          "With Flexible Booking™, change your seat, showtime, or theater anytime up to an hour before the movie starts. To modify, click the link in your confirmation email or visit 'My Tickets' on Angel.com. Flexible Booking is only available at Angel.com.",
        )}
        icon={<CalendarIcon color="oxide-bright" size={20} />}
        triggerComponent={
          <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
            <CalendarIcon color="core-gray-800" size={14} />
            <CaptionMD color="core-gray-800">{t('flexibleBooking', 'Flexible Booking™')}</CaptionMD>
          </span>
        }
      />
      {!!guildTicketsCount && guildTicketsCount > 0 && (
        <Popover
          className="!z-10"
          title={t('redeemGuildTickets', 'Redeem Guild Tickets')}
          description={t(
            'redeemGuildTicketsDescription',
            'Your Guild membership includes complimentary movie tickets that can be redeemed at this theater. Select a showtime and your Guild tickets will be applied at checkout. Only available with Angel Partner Theaters at Angel.com.',
          )}
          icon={<CircleStarIcon color="warm-03" size={20} />}
          triggerComponent={
            <span className="relative flex items-center justify-center gap-1 rounded-full border border-core-gray-300 px-3 py-0.5">
              <GuildSolidIcon color1="core-gray-800" color2="core-gray-200" size={14} />
              <CaptionMD color="core-gray-800">{t('redeemGuildTickets', 'Redeem Guild Tickets')}</CaptionMD>
            </span>
          }
        />
      )}
      {promoType && discount > 0 && <DiscountPill promoType={promoType} discount={discount} />}
    </FlexRow>
  )
}

export default VenuePills
